import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UtmInterceptorService {
  private readonly UTM_STORAGE_KEY = 'utm_source';
  private utmSource: string | null = null;

  constructor(private router: Router) {
    // Initialization will be done via the initialize() method called from app initializer
  }

  /**
   * Capture utm_source from initial URL or localStorage
   */
  public initialize(): void {
    this.initializeUtmSource();
    this.interceptNavigation();
  }

  private initializeUtmSource(): void {
    // Check URL first
    const urlParams = new URLSearchParams(window.location.search);
    const urlUtmSource = urlParams.get('utm_source');

    if (urlUtmSource) {
      this.setUtmSource(urlUtmSource);
    } else {
      // Check localStorage
      this.utmSource = localStorage.getItem(this.UTM_STORAGE_KEY);

      // If we have utm_source in localStorage but not in URL, add it to current URL
      if (this.utmSource) {
        this.addUtmToCurrentUrl();
      }
    }
  }

  /**
   * Store utm_source in localStorage
   */
  private setUtmSource(source: string): void {
    this.utmSource = source;
    localStorage.setItem(this.UTM_STORAGE_KEY, source);
  }

  /**
   * Add utm_source to current URL if missing
   */
  private addUtmToCurrentUrl(): void {
    if (typeof window !== 'undefined') {
      const currentUrl = window.location.href;
      const newUrl = this.addUtmToUrl(currentUrl);

      if (newUrl !== currentUrl) {
        // Use replaceState to avoid adding to browser history and page reload
        const urlObj = new URL(newUrl);
        window.history.replaceState(null, '', urlObj.pathname + urlObj.search);
      }
    }
  }

  /**
   * Intercept all router navigation and add utm_source if missing
   */
  private interceptNavigation(): void {
    // Override router navigate method
    const originalNavigate = this.router.navigate.bind(this.router);
    this.router.navigate = (commands: any[], extras: any = {}) => {
      if (this.utmSource) {
        extras.queryParams = extras.queryParams || {};
        if (!extras.queryParams.utm_source) {
          extras.queryParams.utm_source = this.utmSource;
        }
      }
      return originalNavigate(commands, extras);
    };

    // Override router navigateByUrl method
    const originalNavigateByUrl = this.router.navigateByUrl.bind(this.router);
    this.router.navigateByUrl = (url: string, extras: any = {}) => {
      if (this.utmSource && typeof url === 'string') {
        url = this.addUtmToUrl(url);
      }
      return originalNavigateByUrl(url, extras);
    };

    // Listen for route changes to capture new utm_source
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const urlParams = new URLSearchParams(event.urlAfterRedirects.split('?')[1] || '');
      const newUtmSource = urlParams.get('utm_source');
      if (newUtmSource && newUtmSource !== this.utmSource) {
        this.setUtmSource(newUtmSource);
      } else if (!newUtmSource && this.utmSource) {
        // If we lost utm_source but have it stored, add it back
        const currentUrl = event.urlAfterRedirects;
        const newUrl = this.addUtmToUrl(currentUrl);
        if (newUrl !== currentUrl) {
          setTimeout(() => {
            this.router.navigateByUrl(newUrl, { replaceUrl: true });
          }, 0);
        }
      }
    });
  }

  /**
   * Add utm_source to URL string if not present
   */
  private addUtmToUrl(url: string): string {
    if (!this.utmSource) return url;

    // Handle different URL formats
    let fullUrl = url;

    // If it's a relative URL, make it absolute for processing
    if (url.startsWith('/')) {
      fullUrl = `${window.location.origin}${url}`;
    } else if (!url.includes('://')) {
      // Handle relative URLs that don't start with /
      const currentPath = window.location.pathname;
      const basePath = currentPath.endsWith('/') ? currentPath : currentPath + '/';
      fullUrl = `${window.location.origin}${basePath}${url}`;
    }

    const [baseUrl, existingQuery] = fullUrl.split('?');
    const urlParams = new URLSearchParams(existingQuery || '');

    if (!urlParams.has('utm_source')) {
      urlParams.set('utm_source', this.utmSource);
    }

    const queryString = urlParams.toString();
    const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    // If original URL was relative, return it as relative
    if (url.startsWith('/') || !url.includes('://')) {
      try {
        const urlObj = new URL(newUrl);
        return urlObj.pathname + urlObj.search;
      } catch {
        return newUrl;
      }
    }

    return newUrl;
  }

  /**
   * Get current utm_source
   */
  public getUtmSource(): string | null {
    return this.utmSource;
  }

  /**
   * Clear utm_source from memory and localStorage
   * Call this method during logout
   */
  public clearUtmSource(): void {
    this.utmSource = null;
    localStorage.removeItem(this.UTM_STORAGE_KEY);
    console.log('UTM source cleared');
  }

  /**
   * Public method to navigate with utm_source preserved
   * Use this for manual window.location.href assignments
   */
  public navigateWithUtm(url: string): void {
    if (this.utmSource && typeof url === 'string') {
      url = this.addUtmToUrl(url);
    }
    window.location.href = url;
  }
}
